import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useEffect } from "react";
import { sharedMutators } from "../../shared/mutators";
import { namesMatch } from "../../shared/normalizeName";
import { queries } from "../../shared/queries";
import { DEFAULT_WORKFLOW_NAME } from "../../shared/workflowName";
import { startOnboarding } from "../hooks/useOnboarding";
import { useCurrentUserId } from "../hooks/useCurrentUserId";
import {
  clearPostSignupFormViewRecord,
  hasRecordedPostSignupFormView,
  markPostSignupFormViewRecorded,
} from "../lib/postSignupFormView";
import {
  clearSignupAttribution,
  readSignupAttribution,
} from "../lib/signupAttribution";
import { isRouteLocation, navigate, routes } from "../lib/urls";
import { CompleteProfile } from "./CompleteProfile";
import { markCameFromSignupFlow } from "./NewUserIntroVideo";
import type { SignupRole } from "./Onboarding/onboardingPrompts";

export function SignupProfileForm({
  user,
  tenant,
  onboardingWorkflowEnabled,
}: {
  user: { name: string; email: string };
  tenant: { companyName: string | null };
  onboardingWorkflowEnabled: boolean;
}) {
  const z = useZero();
  const userId = useCurrentUserId();
  const [personalSpace] = useQuery(
    onboardingWorkflowEnabled ? queries.personalSpace() : null
  );
  // SSO gives us one combined name (or the email as a fallback when the IdP
  // asserts none); split it into first/last to prefill, dropping the
  // email-fallback case so we don't seed the fields with an address.
  const ssoName = namesMatch(user.name, user.email) ? "" : user.name;
  const [ssoFirstName = "", ...ssoRestName] = ssoName.split(" ");

  // New users start in an empty workflow whose chat walks them through their first build.
  const openOnboardingWorkflow = (spaceId: string, role: SignupRole) => {
    const workflowId = nanoid(12);
    const branchId = nanoid(12);
    z.mutate(
      sharedMutators.createWorkflow({
        spaceId,
        id: workflowId,
        branchId,
        commitId: nanoid(12),
        chatId: nanoid(12),
        userEntryId: nanoid(12),
        name: DEFAULT_WORKFLOW_NAME,
      })
    );
    startOnboarding(workflowId, role);
    // A new user arrives from SSO with no in-app history, so home becomes the entry the workflow’s back link returns to.
    navigate(routes.recents, undefined, { replace: true });
    navigate(routes.space.workflow, { spaceId, workflowId, branchId });
  };

  return (
    <>
      <RecordPostSignupFormView />
      <CompleteProfile
        initialFirstName={ssoFirstName}
        initialLastName={ssoRestName.join(" ")}
        initialCompanyName={tenant.companyName ?? ""}
        companyNameLocked={Boolean(tenant.companyName)}
        onSubmit={(profile) => {
          markCameFromSignupFlow(userId);
          z.mutate(
            sharedMutators.completeProfile({
              ...profile,
              marketingAttribution: readSignupAttribution(),
            })
          );
          clearSignupAttribution();
          clearPostSignupFormViewRecord();
          const { pathname } = window.location;
          if (
            !personalSpace ||
            (!isRouteLocation(routes.index, pathname) &&
              !isRouteLocation(routes.recents, pathname))
          ) {
            return;
          }
          openOnboardingWorkflow(personalSpace.id, {
            department: profile.department,
            seniority: profile.seniority,
          });
        }}
      />
    </>
  );
}

function RecordPostSignupFormView() {
  const z = useZero();
  const userId = useCurrentUserId();
  useEffect(() => {
    if (hasRecordedPostSignupFormView(userId)) return;
    markPostSignupFormViewRecorded(userId);
    z.mutate(
      sharedMutators.recordPostSignupFormView({
        marketingAttribution: readSignupAttribution(),
      })
    );
  }, [z, userId]);
  return null;
}
